/**
 * **One time series you can read a value off.** The single chart primitive on the client detail:
 * tonnage, sleep, a body measurement and each big-three lift all go through here.
 *
 * ── The readout IS the feature ────────────────────────────────────────────────────────────────
 * Owner, 31 August 2026: "cała wartość tego pakietu ma być owszem w wizualiach, ale przede
 * wszystkim w odczytywaniu wartości z wykresu smooth". A chart a trainer has to guess numbers off
 * is decoration. So five properties are requirements here, not polish:
 *
 *   1. **The whole plot is the hit area.** The pointer anywhere over the box selects the nearest
 *      point ON THE X AXIS. Nobody aims at a 7px mark.
 *   2. **A crosshair and a highlighted mark** say WHICH point the figure belongs to.
 *   3. **The value and its date together.** The axis is time and the spacing is uneven, so a bare
 *      number names nothing.
 *   4. **Frame-paced, not event-paced.** `useScheduledTooltip` coalesces pointer events into one
 *      `requestAnimationFrame` and drops a frame that resolves to the point already shown, so a
 *      drag across the plot commits about one render per frame rather than one per mousemove.
 *      Nothing recomputes the series on a move: the rows, the scales and the formatters are all
 *      built above the interaction and only read by it.
 *   5. **A hole tells the truth.** A week nobody reported says so in the panel. It is never
 *      interpolated and never shown as a zero — the same rule the LINE follows.
 *
 * ── What is bklit's and what is ours ──────────────────────────────────────────────────────────
 * bklit supplies the machinery: the time and value scales, the `defined`-aware path and area, the
 * grid, the nearest-point resolution, the crosshair, the marks and the floating panel. Everything
 * with a colour or a word in it is ours, for the reason this app always gives — the library
 * supplies behaviour, we supply every visual byte.
 *
 * Three of bklit's own display pieces are deliberately NOT used, each for a stated reason:
 *   · **`XAxis`, and every formatter in `chart-formatters.ts`, is hardcoded `en-US`.** This app is
 *     Polish. `AxisLabels` below draws both axes from the chart's real scales, so a label cannot
 *     drift from the pixel it names, and it prints the trainer's own locale.
 *   · **`tooltip/date-ticker.tsx` hardcodes `bg-zinc-900` / `dark:bg-zinc-100`** — not a token, and
 *     wrong in both themes. `showDatePill={false}`; the date is in the panel, where it belongs.
 *   · **`RingCenter`** renders through `@number-flow/react`, which our CSP refuses. See
 *     `ReadoutRing`.
 *
 * ── The y axis is a decision the CALLER makes ─────────────────────────────────────────────────
 * `zeroBaseline` picks between the two honest answers. Tonnage and a set count have a real zero
 * and the mark's length is the figure, so the axis starts there. Bodyweight, sleep and a one-rep
 * max do not: 79–82 kg on an axis of 0–91 is a flat line in the top tenth of the box, and the
 * picture then says only "the number is large", which the figure above it already said. Never a
 * heuristic on the data — the honesty condition belongs to whoever knows what the quantity is.
 *
 * ── Accessibility: the table is the path, and the keyboard drives the same readout ────────────
 * The plot is one `role="img"` whose name carries the SHAPE, and a visually hidden table beside
 * it — never inside it, because `role="img"` swallows its subtree — carries every reading. The
 * plot is also a tab stop: arrows walk the points, Home and End jump to the ends, and the same
 * crosshair, mark and panel move with them, with the reading announced politely.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { chartSummary } from '~/shared/lib/chartSummary';
import {
  countTicks,
  fittedYDomain,
  SERIES_DATE_KEY,
  SERIES_VALUE_KEY,
  tickFractionDigits,
  timeTicks,
  toChartSeries,
  type ChartPoint,
} from '~/shared/lib/chartSeries';
import { numberFormat } from '~/shared/lib/intlCache';
import { OWNS_ARROWS_ATTRIBUTE } from '~/shared/lib/ownsArrows';
import { AreaChart, Area } from '~/shared/vendor/bklit/area-chart';
import { useChart, useChartStable } from '~/shared/vendor/bklit/chart-context';
import { Grid } from '~/shared/vendor/bklit/grid';
import { ReferenceArea } from '~/shared/vendor/bklit/reference-area';
import { StaticSeriesPointMarker } from '~/shared/vendor/bklit/series-point-marker';
import { ChartTooltip } from '~/shared/vendor/bklit/tooltip/chart-tooltip';

import srOnly from '../LiveRegionAnnouncer.module.css';
import { useReadoutSync } from './readoutSync';
import styles from './ReadoutChart.module.css';

/**
 * The space reserved around the plot for its axis labels, in pixels.
 *
 * Numbers rather than tokens because bklit computes the inner box from them in JavaScript, and the
 * labels that live in that reserved space are positioned from the same numbers — so the gutter and
 * the plot cannot disagree about where the box ends.
 */
export const READOUT_MARGIN = { top: 6, right: 8, bottom: 20, left: 46 } as const;
/** A big-three card's plot is 84px tall, and a narrower gutter because a lift's figures are short. */
const MARGIN_COMPACT = { top: 6, right: 8, bottom: 18, left: 40 } as const;

/** The space reserved around a plot, in pixels. bklit computes the inner box from it. */
export interface ReadoutMargin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/** One reading's own mark: how big, and whether it is solid or a ring over the card's fill. */
export interface ReadoutMark {
  readonly radius: number;
  readonly filled: boolean;
}

/**
 * **How a plot is drawn, when the default is not what a canon asks for.**
 *
 * One optional prop rather than nine, and the whole of it is opt-in: a chart that passes nothing
 * draws exactly what every chart in this app drew before 19 September 2026, down to the pixel. It
 * exists because the Pomiary tab's specimen is a full-page plot with its own gutter, its own axis
 * spelling and a mark that differs per reading, and the four other consumers of this component —
 * Trendy, Rekordy, the surveys strip and the preview panel — must not move by a hair for it.
 *
 * Every member is a DECISION the caller is the only one able to make: how much room the grips above
 * the plot need, whether the quantity's axis wants a unit, whether the x axis is months or a short
 * span, which readings were taken by a trainer. None of it is derivable here.
 */
export interface ReadoutPlotSpec {
  readonly margin: ReadoutMargin;
  /** How many horizontal rules and y labels to aim for. */
  readonly yTicks: number;
  /**
   * `'unit'` prints the gutter through the chart's own value formatter — one decimal and the unit.
   * `'bare'` prints the figure alone, up to one decimal, so a round tick reads `70` and not `70,0 kg`.
   */
  readonly yTickStyle: 'unit' | 'bare';
  /**
   * `true` prints both axes in the small tertiary gutter every card chart on the Trendy tab wears
   * (`.yTick`, 9.5 / 600) rather than the legible one a `plot` spec otherwise gets (`.tickCanon`).
   * For a plot that has to read as one family with those charts — the task card's sleep and energy.
   * Omitted, the legible gutter.
   */
  readonly quietTicks?: boolean;
  /**
   * `'span'` spreads five evenly-spaced day-and-month labels across the domain.
   * `'months'` names the first of each month, and falls back to weekly day-and-month labels on a
   * span under ten weeks, where month names would be two labels for a quarter of a year of data.
   */
  readonly xTicks: 'span' | 'months';
  /** Whether the wash under the line is drawn. */
  readonly area: boolean;
  /**
   * The wash's opacity at the top of the plot and at its floor, where a canon asks for a stronger
   * one than KANON's default 0.16 → 0.02 — the plan overview's exercise cards (0.26 → 0). Omitted,
   * the default.
   */
  readonly wash?: { readonly top: number; readonly bottom: number };
  readonly strokeWidth: number;
  /**
   * The mark one reading wears, looked up by its INSTANT rather than by its index: the series is
   * sorted and trimmed inside this component, so an index here would name a different reading than
   * the caller meant the moment a leading hole appeared. `null` draws no mark for that reading.
   */
  readonly markAt?: (at: Date) => ReadoutMark | null;
  /**
   * Values ruled across the whole plot as a dashed line — a level the readings are judged against,
   * as the task card's low-sleep and low-energy plots draw the trainer's own. bklit's
   * `ReferenceArea` as a line (`y1 === y2`), placed by the chart's own scale, so it stands on the
   * pixel its value occupies while the domain tweens and over a `span`'s ground. The axis does not
   * widen for them by itself: pass them in `extraYValues` too.
   */
  readonly levels?: readonly number[];
  /**
   * A stretch of the x axis set apart — the readings a verdict counts. bklit's `ReferenceArea` draws
   * a neutral ground behind it, reaching `SPAN_GROUND_PAD` past each end so a lone reading still
   * stands on one, and, when `mean` is given, the figure the verdict reads as a solid line across
   * the stretch with its label in the right gutter. The label is spelled by the caller; the chart
   * never averages anything.
   */
  readonly span?: ReadoutSpan;
  /**
   * Extra lines under the figure in the readout — the change since the previous reading, who
   * recorded it, whether a verdict counts it. Looked up by instant, for the same reason. They are
   * facts about the reading, so they reach all three channels: the hover panel, the sentence the
   * keyboard walk announces, and a third column of the hidden table.
   */
  readonly notesAt?: (at: Date) => readonly string[];
  /**
   * `false` draws the plot alone — no rules and no labels on either axis. A sparkline: the plan
   * overview's metric strip and its attention rows draw a 30px trend beside a figure that already
   * says the number, and a gutter there would be wider than the line. Omitted, both axes are drawn.
   */
  readonly axes?: boolean;
  /**
   * How an INSTANT is named wherever the chart would print its date — the hover readout, the hidden
   * table, the keyboard announcement and, with `xTickValues`, the x labels. The plan overview reads
   * weeks as „Tydz. 7" and a self-paced plan's cycles have no date at all (their instants are a
   * synthetic step, see `pages/assigned-plan-calendar/model/overviewCycles`), so a day and a month
   * there would name something that never happened. Omitted, the day and the month.
   */
  readonly xAt?: (at: Date) => string;
  /** The x labels to draw, named by `xAt`, in place of the `xTicks` spelling. */
  readonly xTickValues?: readonly Date[];
  /**
   * `false` takes the plot out of the tab order. For a SPARKLINE beside a figure that already says
   * the number: a strip of seven and a card of three put ten keyboard stops between the trainer and
   * the next control, each walking a trend the words above it summarise. The plot keeps its
   * accessible name and its hidden data table, so nothing is lost to a screen reader; only the
   * keyboard walk is. Omitted, the plot is a tab stop, as every other chart here is.
   */
  readonly focusable?: boolean;
}

/** See `ReadoutPlotSpec.span`. */
export interface ReadoutSpan {
  /** The first and the last instant set apart; the same one where there is one reading. */
  readonly from: Date;
  readonly to: Date;
  /** A value drawn as a line across the stretch, and its label. `null` draws the ground alone. */
  readonly mean: { readonly value: number; readonly label: string } | null;
}

/** KANON's area: a wash, not a glow. Top and bottom of the fill gradient. */
const AREA_TOP_OPACITY = 0.16;
const AREA_BOTTOM_OPACITY = 0.02;
/** KANON's 1.6px non-scaling stroke — the line every series here is drawn in. */
export const READOUT_STROKE_WIDTH = 1.6;
const STROKE_WIDTH = READOUT_STROKE_WIDTH;
/** KANON's 7px mark: a 2px ring in the series ink over the card's own fill. */
const MARK_RADIUS = 1.5;
const MARK_RING = 2;
/**
 * A `'dots'` mark: the canon's small quiet FILLED dot, `r="2"` in the Pomiary specimen.
 *
 * It is not the ring above worn at a different size. A ring is the mark on a line the eye is meant
 * to follow; under a trend the raw readings are the scatter the smoothed line was drawn THROUGH,
 * and the canon draws them as solid dots at tertiary weight so the line stays the loudest thing in
 * the plot. `seriesAs: 'dots'` inherited the ring geometry and drew eleven hollow rings at full
 * series weight, which is the opposite emphasis.
 */
const DOT_RADIUS = 2;
const DOT_FILL = 'var(--text-3)';
/** A hollow mark's ring, where the caller sizes the mark itself. The canon's own 1.4px. */
const MARK_MIN_RING = 1.4;
/** The canon's hovered mark: `r="4.5"`, solid, over a 2px ring of the card's own fill. */
const CANON_HOVER_DOT = 4.5;
/** How long bklit takes to sweep the series in. The handoff draws its line in 900ms. */
const DRAW_MS = 900;
/**
 * The provisional tail's dash: 5 on 4.
 *
 * A literal rather than a token because bklit takes it as a JavaScript string and hands it to
 * `stroke-dasharray` itself — there is no CSS declaration here for a custom property to live in.
 */
const DASH_TAIL = '5,4';
/** A `levels` line: the dashed rule the legend's „próg" swatch draws. Same reason for a literal. */
const LEVEL_DASH = '4,4';
const LEVEL_WIDTH = 1.25;
/** How far a `span`'s ground and its mean reach past the first and the last instant, in pixels. */
const SPAN_GROUND_PAD = 12;
const SPAN_MEAN_PAD = 10;
/** The gap between the ground's right edge and the mean's label. */
const SPAN_LABEL_GAP = 6;
/** The mean's line: heavier than the series, so it reads as the verdict's figure. */
const SPAN_MEAN_WIDTH = 2.5;

interface ReadoutChartProps {
  /**
   * One class for the chart's own root, on top of its figure treatment. It exists for the
   * skeleton hand-over — `useSkeletonGate` hands out `skel.reveal` and it has to land on the
   * element the answer brings with it, which for three of this app's cards is this chart. The
   * caller supplies no geometry through it; the figure's box is still this component's.
   */
  readonly className?: string;
  readonly points: readonly ChartPoint[];
  /**
   * The series ink, as a token reference — `var(--chart-series-neutral)`. Every colour on the plot
   * derives from it, so the two themes are the cascade's problem rather than this component's.
   */
  readonly colour: string;
  /** The plot box's height, as a token reference. The canon calibrates three of them. */
  readonly height: string;
  /** The noun this chart is about. The eyebrow's own word, so both channels name one thing. */
  readonly name: string;
  /**
   * The unit every figure carries — `t`, `h`, `kg`, `cm`. It reaches the gutter, the hover panel
   * and the hidden table through one formatter, so the three cannot spell a quantity differently.
   *
   * Optional in the type and supplied by every caller today. A chart given none prints bare
   * numbers everywhere rather than a unit in some places and not others.
   */
  readonly unit?: string;
  /**
   * How a TIME is spelled, in seconds: a clock, `1:30`, or a pace, `5:02`. Given, it prints every
   * figure the chart shows (the gutter, the hover panel and the hidden table) with `unit` after it
   * where there is one, and the y axis stands on clock steps (`timeTicks`) rather than decimal ones.
   */
  readonly figure?: (value: number) => string;
  /**
   * The quantity is a count of whole things — visitors, fillers. Every figure prints without a
   * decimal and the y axis stands on whole 1-2-5 steps (`countTicks`), so two visitors never read
   * as „2,0" or rule a line at „1,5". Its axis is labelled in the legible gutter (`.tickCanon`): a
   * count is read off the axis, and the small tertiary label measured 2.55:1 in Flame.
   */
  readonly whole?: boolean;
  /**
   * Which end of the y axis is better. `'lower'` turns the axis over, so a pace that drops draws
   * UP: the plot's top stays the good direction and the wash falls toward the worse one. Omitted,
   * higher is better, as on every other chart here.
   */
  readonly better?: 'higher' | 'lower';
  /**
   * Whether the quantity's zero is a real baseline. `true` for a count or a total; `false` for
   * bodyweight, sleep or a one-rep max, whose axis is fitted to the readings. See the header.
   */
  readonly zeroBaseline: boolean;
  /** A compact plot — a big-three card. Smaller gutter, fewer ticks. */
  readonly compact?: boolean;
  /**
   * Whether the LAST reading covers a period that has not finished — a week measured on its
   * Tuesday.
   *
   * The card already withholds the delta and says "tydzień w trakcie", because a partial total
   * compared against a whole one is arithmetic about the calendar. The LINE said the opposite: it
   * fell off a cliff into the partial figure and read as a client who had stopped training. The
   * tail is drawn DASHED instead, which is the same thing the words say.
   */
  readonly provisionalTail?: boolean;
  /**
   * The x extent to draw over, when several charts have to share one.
   *
   * Omitted, the chart fits its axis to its OWN reported span, which is right for a chart standing
   * alone. Side by side that is wrong: three series trimmed to three different spans put the same
   * date at three different pixels, so a crosshair synchronised to the instant appears to point at
   * three different places. The caller decides the shared span, because only the caller knows which
   * charts are being read together — see `pages/client-detail`'s `model/trends`.
   *
   * It never fabricates data. The chart's ROWS are still its own trimmed series, so a series that
   * begins halfway across a shared domain simply starts halfway across the plot, which is the true
   * statement that the client began reporting halfway through the window.
   */
  readonly xDomain?: readonly [Date, Date];
  /**
   * This chart's id inside a `ReadoutSyncProvider`. Given one, hovering a sibling chart moves this
   * chart's readout to the same instant. Omitted, or outside a provider, the chart is on its own.
   */
  readonly syncId?: string;
  /**
   * Paint the line, its fill and its marks by BAND rather than in one ink.
   *
   * Omitted, the whole series is `colour` — which is right for a quantity with no thresholds, and
   * every chart in this app was that until 2 September 2026. Given, the plot reads as a banded
   * chart: the stroke changes colour at the exact height each threshold occupies on THIS chart's
   * axis, so a stretch of line that dips under a cut is inked for exactly the stretch that is under
   * it. See `ChartBanding`.
   */
  readonly banding?: ChartBanding;
  /**
   * How the series itself is drawn.
   *
   * `'line'`, the default and what every chart in this app drew until 18 September 2026: KANON's
   * stroke over its wash, with a mark on every reading.
   *
   * `'dots'` draws the READINGS AS MARKS ALONE — no stroke and no fill — and is for a caller that
   * draws its own line through `children`. The Pomiary tab's Trend toggle is the one: the line it
   * wants is a centred moving average and the dots under it are the raw readings, which is exactly
   * the canon's picture. The readout, the keyboard path and the hidden data table are unaffected by
   * this and keep reading `points`, so what a trainer can read off the plot is always a REAL
   * reading and never a smoothed one.
   *
   * `'bars'` draws each reading as a column from the zero baseline — a COUNT per period (sessions,
   * sets, records), which the plan overview's canon draws as bars because a line between two counts
   * claims values between them that cannot exist. A provisional last period is drawn as an outline,
   * the bar's own form of the dashed tail. Use it with `zeroBaseline`.
   */
  readonly seriesAs?: 'line' | 'dots' | 'bars';
  /**
   * Values the y axis must enclose although nothing draws them as readings.
   *
   * One caller and one reason: a projection's end value stands outside the readings it was
   * extrapolated from, and an axis fitted to the readings alone would put it off the box. Only
   * consulted when `zeroBaseline` is false, because a zero-anchored axis is bklit's own and is not
   * fitted to anything.
   */
  readonly extraYValues?: readonly number[];
  /** Plot geometry and axis spelling. Omitted, the defaults every other chart here draws. */
  readonly plot?: ReadoutPlotSpec;
  /**
   * Overlays drawn INSIDE the plot, above the series, with the chart's own scales.
   *
   * Each child is classified by bklit from its component name, exactly as `BandGradients` and
   * `BandMarks` above are, so a child meant to sit above the series and outside the reveal clip
   * declares `__isPostOverlay`. Pass ELEMENTS rather than a fragment: bklit classifies each direct
   * child on its own, and a fragment is classified once for everything inside it.
   */
  readonly children?: ReactNode;
  /**
   * An overlay drawn INSIDE the plot but UNDER the series, with the chart's own scales — a reference
   * the reading is judged against, which the reading must sit on top of where the two meet. The
   * plan overview's dashed plan line is the one. One element, without `__isPostOverlay`: bklit keeps
   * children in the order given, and this one is given before the series.
   */
  readonly underlay?: ReactNode;
  /** The viewer asked for reduced motion. Required — see `shared/design-system/motion`. */
  readonly reducedMotion: boolean;
}

/**
 * One stripe of a banded plot: the values it covers, and the ink it is drawn in.
 *
 * `from` and `to` are in the SERIES' own units, not pixels — the chart owns the axis and is the only
 * thing that can turn a threshold into a height, and it re-does that on every frame bklit tweens the
 * domain through. Either may be infinite, which is how a band says "and everything below this".
 */
export interface ChartBand {
  readonly from: number;
  readonly to: number;
  /** A token reference — `var(--danger)`. Never a literal; see `CLAUDE.md`. */
  readonly colour: string;
}

/**
 * How a series is banded.
 *
 * **Two members rather than one, and the second is not derivable from the first.** `spans` say
 * where the COLOUR changes: neighbouring stripes meet on one value and the gradient puts a hard
 * stop there. `inkOf` says which side a READING is on, and a reading that sits exactly on a
 * boundary has to land on one side or the other — a mark drawn from the gradient would come out
 * half one colour and half the other. The two agree everywhere except on the boundary itself,
 * which is precisely the case a trainer's round number lands in.
 */
export interface ChartBanding {
  /** Contiguous, ascending, and covering the whole axis. */
  readonly spans: readonly ChartBand[];
  /** The ink for one reading's own mark. */
  readonly inkOf: (value: number) => string;
}

/**
 * KANON prints ONE decimal on every chart it draws — tonnage, sleep, mood, bodyweight, a one-rep
 * max. This was a prop with that default and no caller ever passed anything else, so the number is
 * written where it is used rather than carried through an interface as a variation nobody wants.
 */
const FRACTION_DIGITS = 1;

/**
 * Thousands are grouped from four digits up, „1 250", where Polish would otherwise print „1250" and
 * the chart would spell a figure differently from the table beside it (Forma's session volume in
 * kilograms, owner 24 September 2026).
 */
const GROUPING = 'always';

/** The top bklit gives a zero-based axis: a tenth of headroom over the highest reading. */
function zeroBasedTop(points: readonly ChartPoint[]): number {
  const values = points.map((point) => point.y).filter((y): y is number => y !== null && Number.isFinite(y));
  return values.length === 0 ? 1 : Math.max(0, ...values) * 1.1 || 1;
}

/** A stable empty default, so `extraYValues` does not bust the y-domain memo on every render. */
const EMPTY_EXTRA_Y: readonly number[] = [];

/**
 * `compact` and `provisionalTail` take their defaults in the BODY, not in the parameter list.
 *
 * `@babel/types` 8 dropped `AssignmentPattern` from the `LVal` alias, and
 * `babel-plugin-react-compiler@1.0.0` gates on `isLVal()` — so a destructured default makes the
 * compiler skip the whole component, silently. Measured on the built bundle: this file's three
 * memo scopes all belonged to the two helpers below, and `ReadoutChart` itself had none, so
 * `xDomain`, `markers` and the readout closure were rebuilt on every render and
 * `TimeSeriesChartCore` re-derived its time scale and decimation for three charts on every scale
 * switch. The same trap is in `ReadoutRing`, `EmptyState`, `ClientPreviewPanel` and `RosterTable`;
 * a version bump is not this round's, so the shape is what changes.
 */
export function ReadoutChart({
  points,
  colour,
  height,
  name,
  unit,
  figure,
  whole: wholeProp,
  better,
  zeroBaseline,
  compact: compactProp,
  provisionalTail: provisionalTailProp,
  xDomain,
  syncId,
  banding,
  seriesAs: seriesAsProp,
  extraYValues: extraYValuesProp,
  plot,
  children,
  underlay,
  reducedMotion,
  className,
}: ReadoutChartProps) {
  const compact = compactProp ?? false;
  const whole = wholeProp ?? false;
  const provisionalTail = provisionalTailProp ?? false;
  const seriesAs = seriesAsProp ?? 'line';
  const extraYValues = extraYValuesProp ?? EMPTY_EXTRA_Y;
  const { t, i18n } = useTranslation();
  const tableId = useId();
  /**
   * The two paints a banded plot needs, named off one id.
   *
   * Stripped of React's own punctuation: `useId` returns `«r7»`, and a `url(#…)` reference is safer
   * spelled in the characters an SVG id has always been able to hold.
   */
  const paintId = `readout-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const strokePaintId = `${paintId}-stroke`;
  const fillPaintId = `${paintId}-fill`;
  const strokePaint = banding === undefined ? colour : `url(#${strokePaintId})`;
  const fillPaint = banding === undefined ? colour : `url(#${fillPaintId})`;
  /**
   * The box the HTML layers portal into.
   *
   * bklit puts every chart child inside the plot's `<svg>`, where a `<div>` is not an element at
   * all — its own `XAxis` solves that by reaching for `containerRef.current` during render, which
   * this app's lint refuses and the React Compiler cannot see through. A callback ref into state
   * answers the same need without either: React hands the element over on mount, the value is real
   * state, and the two layers below take it as a PROP rather than reading a ref.
   */
  const [overlay, setOverlay] = useState<HTMLDivElement | null>(null);

  const number = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: whole ? 0 : FRACTION_DIGITS,
        maximumFractionDigits: whole ? 0 : FRACTION_DIGITS,
        useGrouping: GROUPING,
      }),
    [i18n.language, whole],
  );
  const dayMonth = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }),
    [i18n.language],
  );

  const formatValue = useCallback(
    (value: number) => {
      const spelled = figure === undefined ? number.format(value) : figure(value);
      return unit === undefined || unit === '' ? spelled : `${spelled} ${unit}`;
    },
    [number, unit, figure],
  );
  const xAt = plot?.xAt;
  const formatDate = useCallback(
    (at: Date) => (xAt === undefined ? dayMonth.format(at) : xAt(at)),
    [dayMonth, xAt],
  );
  const drawsAxes = plot?.axes !== false;
  /** Neither a line nor a wash: the marks alone, or the caller's bars. */
  const bare = seriesAs === 'dots' || seriesAs === 'bars';

  const series = useMemo(() => toChartSeries(points), [points]);
  /**
   * The axis, decided once. A fitted domain of `null` — nothing reported — cannot reach the chart,
   * because an empty series is refused below; the fallback is written rather than asserted away.
   */
  const yDomain = useMemo<[number, number] | undefined>(() => {
    // `fittedYDomain` reads the VALUES only, so a value the axis has to enclose without drawing it
    // rides in on a point whose instant is never used.
    const anchor = points[0]?.x ?? new Date(0);
    const fitted = zeroBaseline
      ? null
      : fittedYDomain([...points, ...extraYValues.map((y) => ({ x: anchor, y }))]);
    if (better !== 'lower') return fitted === null ? undefined : [fitted[0], fitted[1]];
    const [low, high] = fitted ?? [0, zeroBasedTop(points)];
    return [high, low];
  }, [zeroBaseline, better, points, extraYValues]);
  /**
   * A time's rules and labels on clock steps, a count's on whole steps. Decimal ticks otherwise,
   * bklit's own.
   */
  const tickCount = plot === undefined ? (compact ? 2 : 3) : plot.yTicks;
  const yTickValues =
    figure !== undefined
      ? [...timeTicks(yDomain ?? [0, zeroBasedTop(points)], tickCount)]
      : whole
        ? [...countTicks(yDomain ?? [0, zeroBasedTop(points)], tickCount)]
        : undefined;

  const summary = useMemo(
    () => chartSummary(points, name, { t, formatValue, formatDate }),
    [points, name, t, formatValue, formatDate],
  );

  const rows = useMemo(() => [...series.rows], [series.rows]);
  const notesAt = plot?.notesAt;
  const span = plot?.span;

  // Nothing reported at all. What an empty card SAYS is the caller's decision; what this refuses
  // to do is draw an axis over an invented span.
  if (series.xDomain === null) return null;

  return (
    <figure
      className={`${String(styles.figure)} ${className ?? ''}`}
      // The provisional tail's own mark. It is a fact about the DATA — the last period is still
      // filling up — and it is the second channel for what the card says in words above the plot.
      data-provisional-tail={provisionalTail ? 'true' : undefined}
    >
      <div className={styles.plot} style={{ blockSize: height }} ref={setOverlay}>
        <AreaChart
          data={rows}
          xDataKey={SERIES_DATE_KEY}
          xDomain={xDomain === undefined ? [series.xDomain[0], series.xDomain[1]] : [xDomain[0], xDomain[1]]}
          yDomain={yDomain}
          margin={plot === undefined ? (compact ? MARGIN_COMPACT : READOUT_MARGIN) : plot.margin}
          aspectRatio="auto"
          style={{ height: '100%' }}
          animationDuration={reducedMotion ? 0 : DRAW_MS}
        >
          {span === undefined ? null : <SpanGround span={span} />}
          {drawsAxes ? (
            <Grid
              horizontal
              numTicksRows={plot === undefined ? (compact ? 2 : 3) : plot.yTicks}
              {...(yTickValues === undefined ? {} : { rowTickValues: yTickValues })}
              strokeDasharray="0"
              vertical={false}
            />
          ) : null}
          {/* After the ground: bklit draws its `Grid` in a layer of its own under every other child,
            * so a level ruled as a grid row would be painted over by the ground. */}
          {(plot?.levels ?? []).map((value) => (
            <LevelLine key={`level-${String(value)}`} value={value} />
          ))}
          {underlay}
          <Area
            dataKey={SERIES_VALUE_KEY}
            stroke={strokePaint}
            fill={fillPaint}
            // `'dots'` leaves the marks standing alone: the caller draws the line it wants as a
            // child. Zero rather than a second component, so nothing about the series' geometry,
            // its readout or its hidden table forks.
            strokeWidth={bare ? 0 : (plot?.strokeWidth ?? STROKE_WIDTH)}
            fillOpacity={bare || plot?.area === false ? 0 : (plot?.wash?.top ?? AREA_TOP_OPACITY)}
            gradientToOpacity={
              bare || plot?.area === false ? 0 : (plot?.wash?.bottom ?? AREA_BOTTOM_OPACITY)
            }
            showHighlight={false}
            // The last SEGMENT is the provisional one, and bklit dashes from the index given to
            // the end of the path — so the index wanted is the point that OPENS that segment,
            // `rows.length - 2`. `- 1` is the last point, and dashing from there dashes nothing.
            dashFromIndex={provisionalTail ? Math.max(0, rows.length - 2) : undefined}
            dashArray={DASH_TAIL}
            // A banded plot draws its own marks below, because bklit takes ONE marker colour and a
            // reading sitting exactly on a threshold has to be inked from its own value rather than
            // from the gradient it stands in. See `BandMarks`.
            // A plot that marks each reading in its own right draws them below, for the same reason
            // a banded one does: bklit takes ONE marker configuration for the whole series.
            showMarkers={banding === undefined && plot?.markAt === undefined && seriesAs !== 'bars'}
            // A ring on a line, a filled dot under a trend — see `DOT_RADIUS`. Only the three
            // values that differ are branched; everything else about a mark stays one decision.
            markers={{
              radius: seriesAs === 'dots' ? DOT_RADIUS : MARK_RADIUS,
              strokeWidth: seriesAs === 'dots' ? 0 : MARK_RING,
              ringGap: 0,
              fill: seriesAs === 'dots' ? DOT_FILL : 'var(--bg-card)',
              stroke: colour,
              // Dimming and enlarging every other mark on hover is bklit's house effect, and it
              // fights the crosshair, which already says which point is selected. Off, so the plot
              // holds still while the readout moves.
              fadeOnHover: false,
              showActiveHighlight: false,
            }}
            animate={!reducedMotion}
          />

          {/* Two children rather than a fragment around them: bklit classifies each child by its
            * component name, and a fragment would be classified once for both. Separately,
            * `BandGradients` is hoisted into the chart's own `<defs>` and `BandMarks` renders with
            * the series inside the reveal clip, which is where each belongs. A `null` is not a
            * valid element and is skipped. */}
          {banding === undefined ? null : (
            <BandGradients fillId={fillPaintId} spans={banding.spans} strokeId={strokePaintId} />
          )}
          {seriesAs === 'bars' ? (
            <SeriesBars colour={colour} provisionalLast={provisionalTail} />
          ) : null}
          {seriesAs === 'bars' || (banding === undefined && plot?.markAt === undefined) ? null : (
            <SeriesMarks
              styleOf={(value, at) => {
                const markAt = plot?.markAt;
                if (markAt === undefined) {
                  return {
                    radius: MARK_RADIUS,
                    fill: 'var(--bg-card)',
                    stroke: banding === undefined ? colour : banding.inkOf(value),
                    strokeWidth: MARK_RING,
                  };
                }
                const mark = markAt(at);
                if (mark === null) return null;
                // The shape is the reading's own; the ink is its band's, as on every banded plot.
                const ink = banding === undefined ? colour : banding.inkOf(value);
                // Filled is the reading itself; hollow is the card's own ground inside the series
                // ink, so the line passes behind the mark rather than through it.
                return mark.filled
                  ? { radius: mark.radius, fill: ink, stroke: ink, strokeWidth: 0 }
                  : {
                      radius: mark.radius,
                      fill: 'var(--bg-card)',
                      stroke: ink,
                      strokeWidth: MARK_MIN_RING,
                    };
              }}
            />
          )}

          {span === undefined || span.mean === null ? null : <SpanMean span={span} value={span.mean.value} />}

          {/* The caller's own overlays, each classified by bklit on its own name — see the prop. */}
          {children}

          {drawsAxes ? (
          <AxisLabels
            compact={compact}
            figure={figure}
            formatValue={formatValue}
            legible={plot?.quietTicks === true ? false : plot !== undefined || whole}
            overlay={overlay}
            plot={plot}
            tickValues={yTickValues}
            unit={unit}
          />
          ) : null}

          <ChartTooltip
            showDatePill={false}
            showDots
            // The canon's hovered mark is SOLID in the series ink over a ring of the card's own
            // fill; everywhere else it is the hollow ring bklit draws by default.
            dotVariant={plot === undefined ? 'ring' : 'dot'}
            dotSize={plot === undefined ? 4 : CANON_HOVER_DOT}
            dotStrokeWidth={2}
            // The hovered point's ring sits on top of the mark below it, so it takes the same ink
            // — its own reading's, never the series'. A hole has no reading and keeps the base.
            dotColor={(point: Record<string, unknown>) => {
              const value = point[SERIES_VALUE_KEY];
              return banding !== undefined && typeof value === 'number' && Number.isFinite(value)
                ? banding.inkOf(value)
                : colour;
            }}
            indicatorColor="var(--chart-crosshair)"
            className={styles.tooltip ?? ''}
            content={({ point }) => (
              <ReadoutPanel
                formatDate={formatDate}
                formatValue={formatValue}
                notesAt={plot?.notesAt}
                point={point}
                wide={plot !== undefined}
              />
            )}
          />

          <ReadoutDriver
            syncId={syncId}
            formatValue={formatValue}
            formatDate={formatDate}
            label={summary.label}
            describedBy={tableId}
            notesAt={notesAt}
            overlay={overlay}
            focusable={plot?.focusable !== false}
          />
        </AreaChart>
      </div>

      {/* A SIBLING of the `role="img"` inside the plot, never a child: a `role="img"` swallows its
        * subtree, so a table in there would be announced as "picture" and nothing else.
        *
        * The visually-hidden rule is on a WRAPPER, not on the table. A `<table>` treats `height` as
        * a MINIMUM and grows to its rows however small the declared box is, so the class on the
        * table itself produced a 1px-wide, 198px-tall absolutely positioned element that pushed the
        * document 7px past the fold at 1280×620 — a whole-page scroll from something nobody can
        * see. A `<div>` honours the 1px box and clips the table inside it. */}
      <div className={srOnly.region ?? ''}>
        <table id={tableId}>
          <caption>{summary.caption}</caption>
          <thead>
            <tr>
              <th scope="col">{t('web:chart.columnDate')}</th>
              <th scope="col">{name}</th>
              {notesAt === undefined ? null : <th scope="col">{t('web:chart.columnNote')}</th>}
            </tr>
          </thead>
          <tbody>
            {summary.rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.date}</th>
                <td>{row.value}</td>
                {/* A row's key is its instant, which is what the notes are looked up by. */}
                {notesAt === undefined ? null : <td>{notesAt(new Date(row.key)).join(', ')}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/**
 * **The banded paint** — one vertical gradient in the plot's own user space, with a hard stop on
 * every threshold.
 *
 * Two gradients rather than one, because the line and its wash want the same hues at the same
 * heights and different transparencies: the stroke is solid, and the fill carries KANON's
 * top-to-bottom wash (`AREA_TOP_OPACITY` to `AREA_BOTTOM_OPACITY`). Both are built from the same
 * offsets, so the fill can never band at a different height from the line above it.
 *
 * ── Why user space, and why the offsets are recomputed every frame ───────────────────────────
 * `gradientUnits="userSpaceOnUse"` resolves in the user space of the element REFERENCING the
 * gradient, which is the series path inside the plot's translated group — so `yScale` maps a
 * threshold straight onto an offset. The default, `objectBoundingBox`, would resolve against each
 * path's own box: the line's box and the area's box differ (the area reaches the baseline), so the
 * two would band at two different heights, and both would move as the data changed.
 *
 * `useChartStable` is read live because bklit TWEENS the y domain — on entry, and on every scale
 * switch. A stop computed once would be right only after the tween settled.
 *
 * ── A threshold outside the axis sticks to the edge; it never becomes a NaN ──────────────────
 * The sleep axis does not start at zero, and a week every night of which was under seven hours has
 * a domain entirely below the cut. `yScale` extrapolates rather than clamping, so a stop for such a
 * threshold lands off the box and is clamped to 0 or 1 — which collapses its stripe to nothing and
 * leaves the neighbouring one covering the plot. That is the honest picture: the whole line red.
 * An infinite bound extrapolates to an infinity, which the same clamp answers.
 *
 * The name matters to bklit: a chart child whose component name contains "Gradient" is hoisted into
 * the chart's own `<defs>` at the svg root. It renders correctly either way — a gradient is never
 * drawn where it stands — but `defs` is where it belongs, and `displayName` survives minification
 * where a function name does not.
 */
function BandGradients({
  fillId,
  spans,
  strokeId,
}: {
  readonly fillId: string;
  readonly spans: readonly ChartBand[];
  readonly strokeId: string;
}) {
  const { yScale, innerHeight } = useChartStable();

  // Before the first measurement there is no box to place a stop in, and every offset would be a
  // division by zero.
  if (innerHeight <= 0) return null;

  const offsetOf = (value: number) => {
    const y = yScale(value);
    // ±Infinity in, ±Infinity out: the top of the box for a value above the axis, the bottom for
    // one below it. The axis is inverted, so a large value is a small y.
    if (!Number.isFinite(y)) return value > 0 ? 0 : 1;
    return Math.min(1, Math.max(0, y / innerHeight));
  };
  const washAt = (offset: number) =>
    AREA_TOP_OPACITY + (AREA_BOTTOM_OPACITY - AREA_TOP_OPACITY) * offset;

  // Top of the plot first, which is the HIGHEST value — so the spans, which ascend, are walked
  // backwards. Each contributes its two ends; neighbours share an offset and the change is an edge.
  const stops = [...spans].reverse().flatMap((span) => [
    { key: `${String(span.from)}-top`, offset: offsetOf(span.to), colour: span.colour },
    { key: `${String(span.from)}-bottom`, offset: offsetOf(span.from), colour: span.colour },
  ]);

  return (
    <>
      <linearGradient
        gradientUnits="userSpaceOnUse"
        id={strokeId}
        x1={0}
        x2={0}
        y1={0}
        y2={innerHeight}
      >
        {stops.map((stop) => (
          <stop key={stop.key} offset={stop.offset} stopColor={stop.colour} />
        ))}
      </linearGradient>
      <linearGradient
        gradientUnits="userSpaceOnUse"
        id={fillId}
        x1={0}
        x2={0}
        y1={0}
        y2={innerHeight}
      >
        {stops.map((stop) => (
          <stop
            key={stop.key}
            offset={stop.offset}
            stopColor={stop.colour}
            stopOpacity={washAt(stop.offset)}
          />
        ))}
      </linearGradient>
    </>
  );
}
BandGradients.displayName = 'BandGradients';

/** How one reading's mark is painted. `null` draws none. */
interface SeriesMarkStyle {
  readonly radius: number;
  readonly fill: string;
  readonly stroke: string;
  readonly strokeWidth: number;
}

/**
 * **A mark per reading, each painted from the reading itself.**
 *
 * bklit's `SeriesMarkers` takes ONE configuration for the whole series, and two callers need more
 * than that. A BANDED plot must ink a mark from its own value, because painting them from the
 * gradient would halve any mark sitting exactly on a threshold — seven hours and eight are the two
 * readings a trainer is most likely to have, and both are boundaries. A plot with a `markAt` spec
 * must size and fill a mark from what the reading IS: the Pomiary canon draws a trainer's
 * measurement solid and a client's check-in hollow and smaller.
 *
 * Each mark is bklit's `StaticSeriesPointMarker`. What is not reproduced is its per-point entrance —
 * these marks stand inside the same reveal clip as the line and are swept in with it, which is one
 * gesture instead of two.
 *
 * A hole draws no mark. It is not a zero, and this is the same rule the line follows.
 */
function SeriesMarks({
  styleOf,
}: {
  readonly styleOf: (value: number, at: Date) => SeriesMarkStyle | null;
}) {
  const { data, xScale, yScale, xAccessor } = useChartStable();

  return (
    <g>
      {data.map((row, index) => {
        const value = row[SERIES_VALUE_KEY];
        if (typeof value !== 'number' || !Number.isFinite(value)) return null;

        const at = xAccessor(row);
        const cx = xScale(at);
        const cy = yScale(value);
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

        const style = styleOf(value, at);
        if (style === null) return null;

        // bklit's own point marker: a fill inside a ring with no gap. The ring is centred where a
        // stroked circle's stroke would be, so `radius` stays the mark's outer measure less half
        // the ring — the geometry every chart here drew before.
        return (
          <StaticSeriesPointMarker
            cx={cx}
            cy={cy}
            fill={style.fill}
            // The rows are a fixed window re-read whole on every fetch, and carry no identity a
            // re-order could follow. The index IS the position.
            key={index}
            radius={Math.max(0, style.radius - style.strokeWidth / 2)}
            ringGap={0}
            stroke={style.stroke}
            strokeWidth={style.strokeWidth}
          />
        );
      })}
    </g>
  );
}

/**
 * A `span`'s x extent in DATA coordinates, `pad` pixels past each end — the shape bklit's
 * `ReferenceArea` takes. Read live from the chart's scale, so the pad stays a pad in pixels while
 * bklit tweens the domain.
 */
function paddedSpan(
  xScale: { (value: Date): number | undefined; invert: (pixel: number) => Date },
  span: ReadoutSpan,
  pad: number,
): { readonly x1: Date; readonly x2: Date } {
  return {
    x1: xScale.invert((xScale(span.from) ?? 0) - pad),
    x2: xScale.invert((xScale(span.to) ?? 0) + pad),
  };
}

/**
 * **The ground under a `span`** — bklit's `ReferenceArea` over the stretch's x range and the whole
 * plot's height, in the sunken surface's ink with no edges. Rendered before the series, so every
 * reading stands on it. bklit clamps it to the plot box.
 */
function SpanGround({ span }: { readonly span: ReadoutSpan }) {
  const { xScale } = useChartStable();
  const { x1, x2 } = paddedSpan(xScale, span, SPAN_GROUND_PAD);
  return <ReferenceArea fadeEdges={false} fill="var(--bg-inset)" strokeWidth={0} x1={x1} x2={x2} />;
}

/** **A `levels` line** — bklit's `ReferenceArea` with `y1 === y2`, dashed, across the whole plot. */
function LevelLine({ value }: { readonly value: number }) {
  return (
    <ReferenceArea
      fadeEdges={false}
      fill="none"
      stroke="var(--text-2)"
      strokeDasharray={LEVEL_DASH}
      strokeStyle="dashed"
      strokeWidth={LEVEL_WIDTH}
      y1={value}
      y2={value}
    />
  );
}

/**
 * **The mean across a `span`** — bklit's `ReferenceArea` with `y1 === y2`, which the vendored copy
 * draws as its one edge (`shared/vendor/AGENTS.md`). Solid, in the page's ink: the verdict's colour
 * stays on the figure in the card's heading, and energy's series is already ember.
 */
function SpanMean({ span, value }: { readonly span: ReadoutSpan; readonly value: number }) {
  const { xScale } = useChartStable();
  const { x1, x2 } = paddedSpan(xScale, span, SPAN_MEAN_PAD);
  return (
    <ReferenceArea
      fadeEdges={false}
      fill="none"
      stroke="var(--text)"
      strokeStyle="solid"
      strokeWidth={SPAN_MEAN_WIDTH}
      x1={x1}
      x2={x2}
      y1={value}
      y2={value}
    />
  );
}

/** A bar's share of the step between two periods. The rest is the gap that separates them. */
const BAR_SHARE = 0.5;
/** The dash a provisional bar's outline is drawn in — the tail's own, for the same reason. */
const BAR_OUTLINE = 1.4;

/**
 * **One column per reading, from the zero baseline** — `seriesAs: 'bars'`.
 *
 * bklit's area chart has no bar series, and a count per period drawn as a line claims values
 * between two periods that cannot exist. The geometry is the chart's own scales, read live like
 * `SeriesMarks` reads them, so a bar stands on the same pixel the crosshair snaps to. The width is
 * half the step between neighbouring periods, so a plot of four weeks and one of fifty-two both
 * keep a visible gap. A hole draws no column; a zero draws none either, which is what a zero is.
 *
 * The LAST period is drawn as an outline when it is still filling up — the dashed tail's meaning in
 * a bar's form: a partial count shown solid reads as a collapse.
 */
function SeriesBars({
  colour,
  provisionalLast,
}: {
  readonly colour: string;
  readonly provisionalLast: boolean;
}) {
  const { data, xScale, yScale, xAccessor, innerWidth, innerHeight } = useChartStable();
  if (innerWidth <= 0 || innerHeight <= 0 || data.length === 0) return null;

  const xs = data.map((row) => xScale(xAccessor(row)));
  let step = innerWidth;
  for (let index = 1; index < xs.length; index += 1) {
    const gap = (xs[index] ?? 0) - (xs[index - 1] ?? 0);
    if (gap > 0) step = Math.min(step, gap);
  }
  const width = Math.max(1, step * BAR_SHARE);
  const base = Math.min(innerHeight, Math.max(0, yScale(0) ?? innerHeight));

  return (
    <g>
      {data.map((row, index) => {
        const value = row[SERIES_VALUE_KEY];
        const cx = xs[index];
        if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return null;
        if (cx === undefined || !Number.isFinite(cx)) return null;
        const top = yScale(value) ?? base;
        const provisional = provisionalLast && index === data.length - 1;
        return (
          <rect
            // The rows are a fixed window re-read whole on every fetch — the index IS the position.
            key={index}
            fill={provisional ? 'none' : colour}
            height={Math.max(0, base - top)}
            rx={1}
            stroke={provisional ? colour : 'none'}
            strokeDasharray={provisional ? DASH_TAIL : undefined}
            strokeWidth={provisional ? BAR_OUTLINE : 0}
            width={width}
            x={cx - width / 2}
            y={Math.min(top, base)}
          />
        );
      })}
    </g>
  );
}

/**
 * The floating panel's contents: the date, then the figure — or the truth about a hole.
 *
 * bklit's own `TooltipContent` reads `(point[key] as number) ?? 0`, so a week nobody reported
 * prints as a zero. That is the same lie the LINE used to draw before the vendored `defined`
 * patch, and it is why this exists rather than the default being configured.
 */
function ReadoutPanel({
  point,
  formatValue,
  formatDate,
  notesAt,
  wide,
}: {
  readonly point: Record<string, unknown>;
  readonly formatValue: (value: number) => string;
  readonly formatDate: (at: Date) => string;
  /** Extra lines under the figure — see `ReadoutPlotSpec`. */
  readonly notesAt?: (at: Date) => readonly string[];
  /** The canon's larger box: a plot that fills a card can afford the specimen's own readout. */
  readonly wide: boolean;
}) {
  const { t } = useTranslation();
  const at = point[SERIES_DATE_KEY];
  const value = point[SERIES_VALUE_KEY];
  const reported = typeof value === 'number' && Number.isFinite(value);
  const notes = at instanceof Date ? (notesAt?.(at) ?? []) : [];

  return (
    <span className={`${String(styles.panel)} ${wide ? String(styles.panelWide) : ''}`}>
      <span className={styles.panelDate}>{at instanceof Date ? formatDate(at) : ''}</span>
      {reported ? (
        <b className={styles.panelValue}>{formatValue(value)}</b>
      ) : (
        <em className={styles.panelGap}>{t('web:chart.noReading')}</em>
      )}
      {notes.map((note) => (
        <span className={styles.panelNote} key={note}>
          {note}
        </span>
      ))}
    </span>
  );
}

/**
 * Both axes, drawn from the chart's OWN scales.
 *
 * Not bklit's `XAxis`: its labels come from `chart-formatters.ts`, three hardcoded `en-US`
 * formatters. Reading `xScale` and `yScale` here places a label at the pixel its value actually
 * occupies, so the gutter cannot drift from the line even while bklit tweens the domain — which it
 * does on every status change.
 *
 * The y labels sit on `yScale.ticks(...)`, which is exactly what `Grid` rules the plot with, so
 * every label stands on a line and every line is named. KANON puts its three values at the top,
 * middle and bottom of the box instead; nice ticks are the chart exemption the owner granted, and
 * they print round numbers rather than a fitted domain's 79,4.
 *
 * `useChartStable` rather than `useChart`: this subscribes to the slice that does NOT change on
 * hover, so moving the pointer never re-renders the axes.
 */
/** The canon's x axis switches to month names above this many days. Its own seventy. */
const MONTH_LABEL_DAYS = 70;
/** Below this many days the weekly labels step down to whole days. */
const SHORT_AXIS_DAYS = 21;
/** About how many day labels a short axis carries. */
const SHORT_AXIS_LABELS = 5;
const DAY_MS = 86_400_000;

function AxisLabels({
  compact,
  figure,
  formatValue,
  legible,
  overlay,
  plot,
  tickValues,
  unit,
}: {
  readonly compact: boolean;
  readonly figure: ((value: number) => string) | undefined;
  readonly formatValue: (value: number) => string;
  /** The labels wear the legible gutter (`.tickCanon`) — a `plot` spec's chart, or a count's. */
  readonly legible: boolean;
  /** The plot box, handed down by `ReadoutChart`. `null` for the first frame only. */
  readonly overlay: HTMLElement | null;
  readonly plot: ReadoutPlotSpec | undefined;
  /** The y ticks when the chart decided them; `yScale`'s own otherwise. */
  readonly tickValues: readonly number[] | undefined;
  readonly unit: string | undefined;
}) {
  const { i18n } = useTranslation();
  const { xScale, yScale, innerWidth, innerHeight, margin } = useChartStable();

  const shortDate = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }),
    [i18n.language],
  );
  const monthName = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: 'short' }),
    [i18n.language],
  );

  if (overlay === null || innerWidth <= 0) return null;

  const yTicks = tickValues ?? yScale.ticks(plot === undefined ? (compact ? 2 : 3) : plot.yTicks);
  /**
   * As many decimals as the ticks' step has, so no two labels print alike. The canon's gutter
   * figure (`bare`) forces no trailing zero, so a round tick reads `70` where `formatValue` would
   * print `70,0 kg`; the unit spelling keeps its one decimal unless the step needs more.
   */
  const digits = tickFractionDigits(yTicks);
  const tickLabel = (value: number): string => {
    if (figure !== undefined) return plot?.yTickStyle === 'bare' ? figure(value) : formatValue(value);
    if (plot?.yTickStyle === 'bare') {
      return numberFormat(i18n.language, { maximumFractionDigits: digits, useGrouping: GROUPING }).format(value);
    }
    if (digits <= FRACTION_DIGITS) return formatValue(value);
    const stepped = numberFormat(i18n.language, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: GROUPING,
    }).format(value);
    return unit === undefined ? stepped : `${stepped} ${unit}`;
  };
  const [from, to] = xScale.domain() as [Date, Date];
  const domainSpan = to.getTime() - from.getTime();

  /**
   * The x labels, and which of the two spellings they wear.
   *
   * `'months'` names the FIRST of each month, which is what puts a label under the same pixel the
   * month actually begins at rather than under an evenly-spaced fraction of the domain. Below ten
   * weeks the same rule would print two labels for a whole quarter of the plot, so it steps down to
   * weekly day-and-month ticks — the canon's own fallback, at its own threshold.
   */
  const monthTicks = (): readonly Date[] => {
    const ticks: Date[] = [];
    const cursor = new Date(from);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    cursor.setMonth(cursor.getMonth() + 1);
    while (cursor.getTime() < to.getTime()) {
      ticks.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return ticks;
  };
  /**
   * Weekly labels, stepping down to whole days on a history shorter than three weeks.
   *
   * A weekly step from the domain's start prints nothing at all on a span under seven days, and the
   * axis then names no date whatever: two readings three days apart were drawn over a bare floor.
   * Below three weeks the step is the fewest whole days that keep about five labels, each on a
   * local midnight so the label stands under the day it names.
   */
  const weekTicks = (): readonly Date[] => {
    const ticks: Date[] = [];
    const days = domainSpan / DAY_MS;
    if (days >= SHORT_AXIS_DAYS) {
      for (let at = from.getTime() + 7 * DAY_MS; at < to.getTime(); at += 7 * DAY_MS) {
        ticks.push(new Date(at));
      }
      return ticks;
    }
    const step = Math.max(1, Math.ceil(days / SHORT_AXIS_LABELS));
    const cursor = new Date(from);
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(cursor.getDate() + 1);
    while (cursor.getTime() < to.getTime()) {
      ticks.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + step);
    }
    return ticks;
  };
  const evenTicks = (): readonly Date[] => {
    const count = compact ? 3 : 5;
    return Array.from(
      { length: count },
      (_, index) => new Date(from.getTime() + (domainSpan * index) / (count - 1)),
    );
  };

  const given = plot?.xTickValues;
  const byMonth = given === undefined && plot?.xTicks === 'months' && domainSpan / DAY_MS >= MONTH_LABEL_DAYS;
  const xTicks =
    given ?? (plot?.xTicks === 'months' ? (byMonth ? monthTicks() : weekTicks()) : evenTicks());
  const centred = given !== undefined || plot?.xTicks === 'months';
  const xLabel = (at: Date): string =>
    given !== undefined && plot?.xAt !== undefined
      ? plot.xAt(at)
      : byMonth
        ? monthName.format(at)
        : shortDate.format(at);

  const span = plot?.span;
  const mean = span?.mean ?? null;

  return createPortal(
    <div className={styles.axes} aria-hidden="true">
      {/* A `span`'s mean, named in the right gutter at its own height — past the ground's edge, so
        * it never stands on a reading. Aria-hidden like every label here: the hidden table and the
        * notes say which readings the figure is taken over. */}
      {span === undefined || mean === null ? null : (
        <span
          className={styles.spanLabel}
          style={{
            insetInlineStart: `${String(margin.left + Math.min(innerWidth, (xScale(span.to) ?? 0) + SPAN_GROUND_PAD) + SPAN_LABEL_GAP)}px`,
            insetBlockStart: `${String(margin.top + (yScale(mean.value) ?? 0))}px`,
          }}
        >
          {mean.label}
        </span>
      )}
      {yTicks.map((value) => (
        <span
          key={`y-${String(value)}`}
          className={`${String(styles.yTick)} ${legible ? String(styles.tickCanon) : ''}`}
          style={{
            insetInlineEnd: `calc(100% - ${String(margin.left - 8)}px)`,
            insetBlockStart: `${String(margin.top + (yScale(value) ?? 0))}px`,
          }}
        >
          {tickLabel(value)}
        </span>
      ))}
      {xTicks.map((at, index) => (
        <span
          key={`x-${at.toISOString()}`}
          className={`${String(styles.xTick)} ${legible ? String(styles.tickCanon) : ''}`}
          style={{
            insetInlineStart: `${String(margin.left + (xScale(at) ?? 0))}px`,
            insetBlockStart: `${String(margin.top + innerHeight + 4)}px`,
            // Every month label is centred on the first of its month. The evenly-spaced spelling
            // pins its END labels instead, which would otherwise hang off the box.
            transform: centred
              ? 'translateX(-50%)'
              : index === 0
                ? 'none'
                : index === xTicks.length - 1
                  ? 'translateX(-100%)'
                  : 'translateX(-50%)',
          }}
        >
          {xLabel(at)}
        </span>
      ))}
    </div>,
    overlay,
  );
}

/**
 * Everything that makes the plot a control: the accessible name, the tab stop, the arrow keys, and
 * the two-way link to the other charts on the tab.
 *
 * A CHILD of the chart rather than a wrapper around it, because all of it needs the chart's scales
 * and its hover state, and those live in a context the chart itself provides.
 */
function ReadoutDriver({
  syncId,
  formatValue,
  formatDate,
  label,
  describedBy,
  notesAt,
  overlay,
  focusable,
}: {
  /** See `ReadoutPlotSpec.focusable`. */
  readonly focusable: boolean;
  /** See `ReadoutPlotSpec.notesAt`. */
  readonly notesAt: ((at: Date) => readonly string[]) | undefined;
  readonly syncId: string | undefined;
  readonly formatValue: (value: number) => string;
  readonly formatDate: (at: Date) => string;
  readonly label: string;
  readonly describedBy: string;
  /** The plot box, handed down by `ReadoutChart`. `null` for the first frame only. */
  readonly overlay: HTMLElement | null;
}) {
  const { t } = useTranslation();
  const { data, xScale, yScale, xAccessor, tooltipData, setTooltipData, innerWidth, innerHeight, margin } =
    useChart();
  const sync = useReadoutSync();
  const [announcement, setAnnouncement] = useState('');

  /**
   * One row, in the shape bklit's crosshair, mark and panel all read.
   *
   * `yPositions` is left EMPTY for a hole, which is exactly what the pointer path does — and it is
   * why the vendored `chart-tooltip.tsx` had to learn to skip a dot for a series with no position
   * rather than drawing it at pixel 0, the top of the plot.
   */
  const frameAt = useCallback(
    (index: number) => {
      const row = data[index];
      if (row === undefined) return null;
      const value = row[SERIES_VALUE_KEY];
      const yPositions: Record<string, number> =
        typeof value === 'number' && Number.isFinite(value)
          ? { [SERIES_VALUE_KEY]: yScale(value) ?? 0 }
          : {};
      return { point: row, index, x: xScale(xAccessor(row)) ?? 0, yPositions };
    },
    [data, xScale, yScale, xAccessor],
  );

  /* ── the sync link ──────────────────────────────────────────────────────────────────────── */

  /**
   * **A FOLLOWER MUST NOT PUBLISH**, and this ref is the whole of that rule.
   *
   * Measured on the running app, 31 August 2026. Two charts on Trendy share a week axis but not
   * their spans: sleep's series is trimmed at its last REPORTED week, tonnage's runs to the current
   * one. Hovering tonnage's last week published 31 August; sleep rounded that to the nearest week
   * it has, 24 August, and published THAT back; tonnage was not the source of that frame, so it
   * followed — and the pointer's own reading was dragged a week backwards. The same pull hid the
   * first point of tonnage behind sleep's leading gap. Ownership stops the loop from running
   * forever; it does not stop one pull, because the two ids genuinely differ.
   *
   * So a chart publishes the readings it OWNS and stays silent about the ones it was handed. The
   * flag is set immediately before the follower's `setTooltipData` and spent by the effect that
   * commit schedules, which is the next thing to run.
   */
  const following = useRef(false);
  // What this chart shows, published for the others. A ref rather than a state round trip: putting
  // the pointer's instant into React state here is exactly what `readoutSync` exists to avoid.
  const lastPublished = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (sync === null || syncId === undefined) return;
    const at = tooltipData === null ? null : tooltipData.point[SERIES_DATE_KEY];
    const stamp = at instanceof Date ? at.getTime() : null;
    if (following.current) {
      following.current = false;
      // Remembered anyway, so a later move back to this same instant under this chart's OWN
      // pointer still counts as a change and is published.
      lastPublished.current = stamp;
      return;
    }
    if (stamp === lastPublished.current) return;
    lastPublished.current = stamp;
    sync.publish({ source: syncId, at: stamp });
  }, [sync, syncId, tooltipData]);

  // What the others show, followed. Frames stamped with this chart's own id are ignored — see
  // `readoutSync`'s header on why ownership rather than value equality settles the loop.
  useEffect(() => {
    if (sync === null || syncId === undefined) return undefined;
    return sync.subscribe((frame) => {
      if (frame.source === syncId) return;
      const at = frame.at;
      if (at === null) {
        following.current = true;
        setTooltipData(null);
        return;
      }
      let nearest = -1;
      let best = Number.POSITIVE_INFINITY;
      for (const [index, row] of data.entries()) {
        const distance = Math.abs(xAccessor(row).getTime() - at);
        if (distance < best) {
          best = distance;
          nearest = index;
        }
      }
      following.current = true;
      setTooltipData(nearest === -1 ? null : frameAt(nearest));
    });
  }, [sync, syncId, data, xAccessor, frameAt, setTooltipData]);

  /* ── the keyboard path ──────────────────────────────────────────────────────────────────── */

  const step = (index: number) => {
    const frame = frameAt(Math.max(0, Math.min(data.length - 1, index)));
    if (frame === null) return;
    setTooltipData(frame);
    const at = frame.point[SERIES_DATE_KEY];
    const value = frame.point[SERIES_VALUE_KEY];
    const reading =
      typeof value === 'number' && Number.isFinite(value)
        ? formatValue(value)
        : t('web:chart.noReading');
    const notes = at instanceof Date ? (notesAt?.(at) ?? []) : [];
    setAnnouncement([`${at instanceof Date ? formatDate(at) : ''}: ${reading}`, ...notes].join(', '));
  };

  if (overlay === null || innerWidth <= 0) return null;

  const current = tooltipData?.index ?? -1;

  return createPortal(
    <>
      {/* The plot's accessible identity and its keyboard path, laid over the box bklit reserved
        * for the series. `pointer-events: none` in the stylesheet is load-bearing: this sits ABOVE
        * the SVG, and without it the pointer never reaches the interaction rect underneath and the
        * whole hover readout is dead. Keyboard focus is unaffected by that property. */}
      <div
        className={styles.surface}
        role="img"
        tabIndex={focusable ? 0 : undefined}
        // The arrows walk the readings, so a screen's own arrow shortcuts stand down here.
        {...(focusable ? { [OWNS_ARROWS_ATTRIBUTE]: '' } : {})}
        aria-label={label}
        aria-describedby={describedBy}
        style={{
          insetInlineStart: `${String(margin.left)}px`,
          insetBlockStart: `${String(margin.top)}px`,
          inlineSize: `${String(innerWidth)}px`,
          blockSize: `${String(innerHeight)}px`,
        }}
        onFocus={() => {
          if (tooltipData === null) step(data.length - 1);
        }}
        onBlur={() => {
          setTooltipData(null);
          setAnnouncement('');
        }}
        onKeyDown={(event) => {
          const from = current === -1 ? data.length - 1 : current;
          if (event.key === 'ArrowRight') step(from + 1);
          else if (event.key === 'ArrowLeft') step(from - 1);
          else if (event.key === 'Home') step(0);
          else if (event.key === 'End') step(data.length - 1);
          else if (event.key === 'Escape') setTooltipData(null);
          else return;
          event.preventDefault();
        }}
      />
      <span className={srOnly.region ?? ''} role="status" aria-live="polite">
        {announcement}
      </span>
    </>,
    overlay,
  );
}
